<script setup>
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AcessoAPI from 'dashboard/api/acesso';
import {
  ehSemAcesso,
  escolhaDoGrupo,
  escoposDoModulo,
  gruposDoModulo,
  semAcesso,
} from './catalogo';
import { fraseDoErro } from './fraseDoErro';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import LinhaDoModulo from './components/LinhaDoModulo.vue';
import DialogoDoGrupo from './components/DialogoDoGrupo.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

// ACESSO-01 (L9a): o editor do papel, como no quadro 2 do mock v3. Nome, descrição e uma linha por módulo do catálogo,
// cada uma com o grupo escolhido e, quando nenhum grupo serve, os ajustes escopo a escopo.
//
// - **Papel novo** (`papeis_novo`): todo módulo começa em "Sem acesso".
// - **Modelo da Powerbizz** (`somente_leitura`): só leitura, sem Salvar. "Duplicar" cria o papel da conta e abre o
//   editor dele; as trocas do grupo só de modelo, que vêm só na resposta do `duplicar`, chegam ao editor da cópia pelo
//   estado da navegação.
// - **"Criar grupo"** abre o grupo novo num Dialog por cima do editor, com o módulo da linha fixo. Criado, ele fica escolhido
//   naquele módulo, e o resto do formulário fica como estava.
// - **A marca da migração** ("Revise este papel") aparece em texto abaixo do topo, com "Marcar como revisado", que a tira
//   na hora (`revisar: false`). Salvar o papel não a tira.
// - **A falha** aparece abaixo do topo: a recusa do acesso (403) com a frase do servidor, e o 422 com a dele.
// - **No modelo**, que é só leitura, a descrição e os grupos são texto: o campo desabilitado ficava a 3,2:1 no tema
//   claro e cortava a descrição no meio, e o seletor desabilitado parecia quebrado. O nome já é o título.
// - **Cancelar, Salvar e Duplicar** ficam na linha do título, à direita, como no mock (o `#titleActions` do
//   `BaseSettingsHeader`). No `#actions`, sem abas nem busca ao lado, eles caíam sozinhos à esquerda, embaixo da
//   descrição.
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const catalogo = useMapGetter('acesso/catalogo');

const { accountId, papelId } = route.params;
const ESCOLHA_SEM_ACESSO = semAcesso();

const papel = ref(null);
const nome = ref('');
const descricao = ref('');
// Por módulo: o grupo escolhido (o módulo sem escolha está em "Sem acesso") e os ajustes (`{ escopo, sentido }`).
const escolhas = ref({});
const ajustes = ref({});
const trocas = window.history.state?.trocas || [];

const carregando = ref(true);
const erroAoCarregar = ref('');
const salvando = ref(false);
const duplicando = ref(false);
const marcandoRevisado = ref(false);
const erro = ref('');

const somenteLeitura = computed(() => papel.value?.somente_leitura === true);
const modulos = computed(() => catalogo.value?.modulos || []);
const doModulo = computed(() =>
  Object.fromEntries(
    modulos.value.map(({ chave }) => [
      chave,
      {
        grupos: gruposDoModulo(catalogo.value, chave),
        escopos: escoposDoModulo(catalogo.value, chave),
      },
    ])
  )
);

const titulo = computed(() =>
  papelId ? papel.value?.nome || '' : t('PAPEIS.LISTA.NOVO')
);
const descricaoDoTopo = computed(() => {
  if (!papel.value) return '';
  const n = papel.value.pessoas;
  const usadoPor = t('PAPEIS.EDITOR.USADO_POR', { n }, n);
  return somenteLeitura.value
    ? `${usadoPor} ${t('PAPEIS.EDITOR.MODELO_SO_LEITURA')}`
    : usadoPor;
});
const lista = { name: 'papeis_lista', params: { accountId } };

const preencher = ficha => {
  papel.value = ficha;
  nome.value = ficha.nome;
  descricao.value = ficha.descricao || '';
  escolhas.value = Object.fromEntries(
    ficha.grupos.map(grupo => [grupo.modulo, escolhaDoGrupo(grupo)])
  );
  ajustes.value = ficha.ajustes.reduce(
    (porModulo, { modulo, escopo, sentido }) => ({
      ...porModulo,
      [modulo]: [...(porModulo[modulo] || []), { escopo, sentido }],
    }),
    {}
  );
};

// O catálogo vem de novo a cada abertura: um grupo criado na aba Grupos entra no seletor.
const carregar = async () => {
  carregando.value = true;
  try {
    const [, resposta] = await Promise.all([
      store.dispatch('acesso/carregarCatalogo', { recarregar: true }),
      papelId ? AcessoAPI.papel(papelId) : null,
    ]);
    if (resposta) preencher(resposta.data);
  } catch (falha) {
    erroAoCarregar.value = fraseDoErro(
      falha,
      t('PAPEIS.EDITOR.ERRO_AO_CARREGAR')
    );
  } finally {
    carregando.value = false;
  }
};

// Um grupo por módulo com acesso (o "Sem acesso" é o módulo ausente) e os ajustes, na ordem do catálogo.
const corpo = () => ({
  nome: nome.value.trim(),
  descricao: descricao.value.trim(),
  grupos: modulos.value.flatMap(({ chave: modulo }) => {
    const escolha = escolhas.value[modulo];
    if (!escolha || ehSemAcesso(escolha)) return [];
    return escolha.origem === 'conta'
      ? [{ modulo, id: escolha.id }]
      : [{ modulo, chave: escolha.chave }];
  }),
  ajustes: modulos.value.flatMap(({ chave }) => ajustes.value[chave] || []),
});

const voltarALista = () => router.push(lista);

const salvar = async () => {
  salvando.value = true;
  erro.value = '';
  try {
    if (papel.value) {
      await AcessoAPI.atualizarPapel(papel.value.id, corpo());
      useAlert(t('PAPEIS.EDITOR.SALVO'));
    } else {
      await AcessoAPI.criarPapel(corpo());
      useAlert(t('PAPEIS.EDITOR.CRIADO'));
    }
    voltarALista();
  } catch (falha) {
    erro.value = fraseDoErro(falha, t('PAPEIS.EDITOR.ERRO_AO_SALVAR'));
  } finally {
    salvando.value = false;
  }
};

const duplicar = async () => {
  duplicando.value = true;
  erro.value = '';
  try {
    const { data } = await AcessoAPI.duplicarPapel(papelId);
    useAlert(t('PAPEIS.EDITOR.DUPLICADO'));
    router.push({
      name: 'papeis_editor',
      params: { accountId, papelId: data.id },
      state: { trocas: data.trocas || [] },
    });
  } catch (falha) {
    erro.value = fraseDoErro(falha, t('PAPEIS.EDITOR.ERRO_AO_DUPLICAR'));
  } finally {
    duplicando.value = false;
  }
};

// Só a marca: o nome, a descrição e o acesso que estão no formulário, gravados ou não, ficam como estão.
const marcarRevisado = async () => {
  marcandoRevisado.value = true;
  erro.value = '';
  try {
    await AcessoAPI.atualizarPapel(papel.value.id, { revisar: false });
    papel.value = { ...papel.value, revisar: false, revisar_motivo: null };
    useAlert(t('PAPEIS.EDITOR.REVISADO'));
  } catch (falha) {
    erro.value = fraseDoErro(falha, t('PAPEIS.EDITOR.ERRO_AO_MARCAR'));
  } finally {
    marcandoRevisado.value = false;
  }
};

const dialogoDoGrupo = useTemplateRef('dialogoDoGrupo');
const criarGrupo = modulo =>
  dialogoDoGrupo.value?.abrir({ modulo, fixo: true });

// O catálogo vem de novo para o grupo entrar no seletor; ele começa sem os ajustes do grupo de antes.
const grupoCriado = async grupo => {
  try {
    await store.dispatch('acesso/carregarCatalogo', { recarregar: true });
  } catch (falha) {
    erro.value = fraseDoErro(falha, t('PAPEIS.EDITOR.ERRO_AO_RECARREGAR'));
    return;
  }
  escolhas.value[grupo.modulo] = { origem: 'conta', id: grupo.id };
  ajustes.value[grupo.modulo] = [];
};

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="t('PAPEIS.CARREGANDO')"
    body-class="w-full max-w-4xl"
  >
    <template #header>
      <div class="flex flex-col w-full max-w-4xl gap-3">
        <BaseSettingsHeader
          :title="titulo"
          :description="descricaoDoTopo"
          :back-button-label="t('PAPEIS.TITULO')"
          :back-url="lista"
        >
          <template v-if="!carregando && !erroAoCarregar" #titleActions>
            <div class="flex gap-2 shrink-0" data-acoes>
              <Button
                v-if="somenteLeitura"
                :label="t('PAPEIS.EDITOR.DUPLICAR')"
                icon="i-lucide-copy"
                size="sm"
                :is-loading="duplicando"
                data-duplicar
                @click="duplicar"
              />
              <template v-else>
                <Button
                  :label="t('PAPEIS.EDITOR.CANCELAR')"
                  variant="faded"
                  color="slate"
                  size="sm"
                  data-cancelar
                  @click="voltarALista"
                />
                <Button
                  :label="t('PAPEIS.EDITOR.SALVAR')"
                  size="sm"
                  :is-loading="salvando"
                  :disabled="!nome.trim() || salvando"
                  data-salvar
                  @click="salvar"
                />
              </template>
            </div>
          </template>
        </BaseSettingsHeader>
        <div
          v-if="papel?.revisar"
          class="flex flex-wrap items-center gap-x-3 gap-y-1"
          data-revisar
        >
          <p class="flex items-start gap-1.5 mb-0 text-sm text-n-slate-11">
            <span
              class="i-lucide-circle-alert size-3.5 mt-1 shrink-0 text-n-amber-11"
              aria-hidden="true"
            />
            <span>
              <span class="font-semibold text-n-slate-12">
                {{ t('PAPEIS.LISTA.REVISAR') }}
              </span>
              {{ papel.revisar_motivo }}
            </span>
          </p>
          <Button
            :label="t('PAPEIS.EDITOR.MARCAR_REVISADO')"
            variant="link"
            color="slate"
            size="sm"
            :is-loading="marcandoRevisado"
            data-marcar-revisado
            @click="marcarRevisado"
          />
        </div>
        <ul
          v-if="trocas.length"
          class="flex flex-col gap-1 m-0 text-sm list-none ps-0 text-n-slate-11"
          data-trocas
        >
          <li v-for="troca in trocas" :key="troca.modulo">
            {{ troca.frase }}
          </li>
        </ul>
        <p
          v-if="erro"
          role="alert"
          class="flex items-start gap-2 mb-0 text-sm text-n-slate-12"
          data-erro
        >
          <span
            class="i-lucide-circle-alert size-4 mt-0.5 shrink-0 text-n-ruby-11"
            aria-hidden="true"
          />
          {{ erro }}
        </p>
      </div>
    </template>
    <template #body>
      <p
        v-if="erroAoCarregar"
        class="py-16 mb-0 text-sm text-center text-n-slate-11"
        data-erro-ao-carregar
      >
        {{ erroAoCarregar }}
      </p>
      <div v-else class="flex flex-col gap-6 py-4">
        <template v-if="somenteLeitura">
          <div v-if="descricao" class="flex flex-col gap-1" data-ficha>
            <span class="text-heading-3 text-n-slate-12">
              {{ t('PAPEIS.EDITOR.DESCRICAO') }}
            </span>
            <p class="mb-0 text-sm max-w-prose text-n-slate-11" data-descricao>
              {{ descricao }}
            </p>
          </div>
        </template>
        <div
          v-else
          class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]"
        >
          <Input
            v-model="nome"
            :label="t('PAPEIS.EDITOR.NOME')"
            :placeholder="t('PAPEIS.EDITOR.NOME_EXEMPLO')"
            custom-input-class="placeholder:!text-n-slate-11 dark:placeholder:!text-n-slate-11"
            data-nome
          />
          <Input
            v-model="descricao"
            :label="t('PAPEIS.EDITOR.DESCRICAO')"
            :placeholder="t('PAPEIS.EDITOR.DESCRICAO_EXEMPLO')"
            custom-input-class="placeholder:!text-n-slate-11 dark:placeholder:!text-n-slate-11"
            data-descricao
          />
        </div>
        <section class="flex flex-col gap-3">
          <div>
            <h2 class="mb-0 text-heading-2 text-n-slate-12">
              {{ t('PAPEIS.EDITOR.ACESSO_POR_MODULO') }}
            </h2>
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                somenteLeitura
                  ? t('PAPEIS.EDITOR.ACESSO_POR_MODULO_DO_MODELO')
                  : t('PAPEIS.EDITOR.ACESSO_POR_MODULO_FRASE')
              }}
            </p>
          </div>
          <!-- As linhas ficam direto no painel, separadas por fios: a lista com borda e raio dentro do painel do
          SettingsLayout era um cartão dentro de outro (o detector da revisão, `nested-cards`). -->
          <ul
            class="m-0 list-none border-t divide-y ps-0 border-n-weak divide-n-weak"
            data-modulos
          >
            <LinhaDoModulo
              v-for="modulo in modulos"
              :key="modulo.chave"
              :modulo="modulo"
              :grupos="doModulo[modulo.chave].grupos"
              :escopos="doModulo[modulo.chave].escopos"
              :escolha="escolhas[modulo.chave] || ESCOLHA_SEM_ACESSO"
              :ajustes="ajustes[modulo.chave] || []"
              :somente-leitura="somenteLeitura"
              @update:escolha="escolha => (escolhas[modulo.chave] = escolha)"
              @update:ajustes="novos => (ajustes[modulo.chave] = novos)"
              @criar-grupo="criarGrupo"
            />
          </ul>
        </section>
      </div>
    </template>

    <DialogoDoGrupo ref="dialogoDoGrupo" @salvo="grupoCriado" />
  </SettingsLayout>
</template>
